// The blend, printed as one ticket — the "Anti-cardslop" design (3a/3b): nothing
// floats in a card; every section is a field on a single ink-outlined sheet,
// divided by perforated tear-lines. Delight is wired in: a filmstrip of the
// shared canon drifts behind the headline (the only loop), the avatars meet, the
// verdict stamp slams in (tap to re-stamp), the ledger counts up, and pick rows
// re-print when you switch modes. All eased, all play once.

import { useMemo, useState } from "react";
import type { DistilledProfile, SimilarityMetrics } from "../lib/types.ts";
import { computeBlend } from "../lib/blend.ts";
import { usePosters } from "../lib/usePosters.ts";
import { useCountUpProgress } from "../lib/useCountUp.ts";
import { IS_TEST } from "../lib/env.ts";
import { BlendMeter, PosterCard } from "./brand.tsx";
import { Masthead } from "./sheet.tsx";
import { PosterRow } from "./Poster.tsx";
import { ShareScoreButton, ShareDisputeButton } from "./ShareCards.tsx";
import { FeelingLucky } from "./FeelingLucky.tsx";

interface Props {
  profileA: DistilledProfile;
  profileB: DistilledProfile;
  nameA: string;
  nameB: string;
  blendSlug?: string;
  summary?: string | null;
  /** True while the combined-taste blurb is being generated in the background. */
  summaryLoading?: boolean;
  /** Members only: force a fresh blurb ("get another"). */
  onRegenerateSummary?: () => void;
  picksSlot?: React.ReactNode;
  shareSlot?: React.ReactNode;
  /** Members only: show the GLM "Tonight's pick" joint suggestion (needs auth). */
  showLucky?: boolean;
}

const TIER_STAMP: Record<string, string> = {
  "twin flames": "Perfect match",
  "great double bill": "Great double bill",
  "opposites with overlap": "Opposites attract",
  "chaos pairing": "Chaos pairing",
};

const firstName = (name: string): string => name.trim().split(/\s+/)[0] || name;
const initial = (name: string): string => (name.trim()[0] || "?").toUpperCase();

export function BlendView({ profileA, profileB, nameA, nameB, blendSlug, summary, summaryLoading, onRegenerateSummary, picksSlot, showLucky }: Props) {
  const blend = useMemo(() => computeBlend(profileA, profileB), [profileA, profileB]);
  const prog = useCountUpProgress();
  const [stampKey, setStampKey] = useState(0);

  const posterFilms = useMemo(
    () => [...blend.watchTonight, ...blend.sharedCanon, ...blend.crossRecsAToB, ...blend.crossRecsBToA],
    [blend],
  );
  const posters = usePosters(posterFilms);

  // Films that drift behind the headline: the shared canon (fall back to either
  // library's top-loved so the strip is never empty).
  const marq = blend.sharedCanon.slice(0, 12);
  const cu = (n: number) => Math.round(n * prog); // count-up helper
  const shownScore = cu(blend.score);

  return (
    <div data-testid="blend-view" className="desk">
      <div className="sheet fadeup">
        <Masthead right="Admit two" sub="Blend" />

        {/* Ink hero — drifting filmstrip, avatars meeting, stamp slam */}
        <section data-testid="section-blend-header" className="heroband bg-ink-900 text-paper-50">
          {marq.length > 0 && (
            <div className="marq" aria-hidden>
              <div className="marqin">
                {[0, 1].map((dup) => (
                  <div className="mset" key={dup}>
                    {marq.map((f) => (
                      <div className="mp" key={`${dup}-${f.uri}`}>
                        <PosterCard title={f.title} year={f.year} poster={posters[f.uri]} width="100%" />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="marqveil" aria-hidden />

          <div className="relative z-[2] flex flex-col items-center gap-4 px-6 py-9 text-center sm:px-10">
            <div className="flex">
              <span className={`avatar-pill bg-red-600 ${IS_TEST ? "" : "meetL"}`}>{initial(nameA)}</span>
              <span className={`avatar-pill -ml-4 border-[3px] border-white bg-gold-500 ${IS_TEST ? "" : "meetR"}`}>
                {initial(nameB)}
              </span>
            </div>
            <h1 className="max-w-xl font-display text-[27px] font-extrabold leading-[1.08] tracking-tightish sm:text-4xl">
              {nameA} <span className="text-gold-500">✕</span> {nameB} are{" "}
              <span data-testid="score-dial" className="tabular-nums">
                {shownScore}
              </span>
              % blended.
            </h1>
            <div className="w-full max-w-sm" style={{ color: "var(--paper-50)" }}>
              <BlendMeter variant="bar" value={blend.score} label="Blend" />
            </div>
            <button
              type="button"
              className="cursor-pointer bg-transparent"
              onClick={() => setStampKey((k) => k + 1)}
              title="Tap to re-stamp"
              aria-label="Re-stamp the verdict"
            >
              <div
                key={stampKey}
                className={IS_TEST ? "" : "stampin"}
                style={{ animationDelay: stampKey === 0 ? "1.05s" : "0s" }}
              >
                <StampInk>{TIER_STAMP[blend.tier] ?? blend.tier}</StampInk>
              </div>
            </button>
            {blend.earlyRead && <span className="font-mono text-[11px] uppercase tracking-stamp text-ink-300">Early read</span>}
            <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
              {blendSlug && <CopyLinkButton slug={blendSlug} />}
              <ShareScoreButton data={{ nameA, nameB, score: blend.score, tier: blend.tier, canon: blend.sharedCanon }} />
            </div>
          </div>
        </section>

        {/* The count — a printed ledger that tallies up */}
        <section data-testid="section-counts" className="px-6 py-6 sm:px-10">
          <div className="flex flex-wrap gap-x-10 gap-y-4">
            <Count n={cu(blend.counts.bothWatched)} label="Shared" />
            <Count n={cu(blend.counts.onlyAWatched)} label={`Only ${firstName(nameA)}`} tone="red" />
            <Count n={cu(blend.counts.onlyBWatched)} label={`Only ${firstName(nameB)}`} tone="gold" />
            <Count n={cu(blend.counts.bothWatchlisted)} label="Both want" tone={blend.counts.bothWatchlisted ? "ink" : "muted"} />
          </div>
        </section>

        <Tear />

        {/* Three sub-scores, divided by vertical perforation */}
        <section data-testid="section-metrics" className="flex flex-col sm:flex-row">
          <ScoreCol pct={cu(blend.metrics.tasteMatch)} label="Rating agreement" desc="How close your star ratings land on films you've both seen." tone="red" />
          <div className="perfv mx-0 hidden sm:block" />
          <ScoreCol pct={cu(blend.metrics.agreementRate)} label="Close calls" desc="Shared films you rated within half a star." tone="gold" />
        </section>

        <Tear />

        {/* Watch tonight */}
        <Field testid="section-watch-tonight">
          <Kicker>On both your watchlists</Kicker>
          <H3>Watch tonight</H3>
          {blend.watchTonight.length ? (
            <PosterRow testid="watch-tonight-row">
              {blend.watchTonight.map((f) => (
                <div key={f.uri} className="w-[120px] shrink-0">
                  <PosterCard title={f.title} year={f.year} poster={posters[f.uri]} width={120} />
                </div>
              ))}
            </PosterRow>
          ) : (
            <Empty>No shared watchlist yet — see the cross-recs below for what to queue.</Empty>
          )}
        </Field>

        <Tear />

        {/* Tonight's pick — a GLM film neither of you has seen (members only) */}
        {showLucky && blendSlug && (
          <>
            <FeelingLucky blendSlug={blendSlug} />
            <Tear />
          </>
        )}

        {/* twoshot picks (tap the modes -> rows re-print) */}
        <section data-testid="section-picks" className="px-6 py-7 sm:px-10">
          <Kicker>Tonight, for two</Kicker>
          <div className="mt-1.5 flex items-center gap-3">
            <H3 inline>twoshot picks</H3>
            <span className="rounded-pill border border-gold-600 px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-stamp text-gold-600">
              ＋ AI-generated
            </span>
          </div>
          <Sub>Recommendations for every region of your Venn.</Sub>
          <div className="mt-4">{picksSlot}</div>
        </section>

        <Tear />

        {/* Shared canon — both people's stars, labeled */}
        <Field testid="section-shared-canon">
          <Kicker>You both loved</Kicker>
          <H3>Your shared canon</H3>
          <Sub>Both rated ★4 or higher — {blend.counts.bothWatched.toLocaleString()} films in common.</Sub>
          {blend.sharedCanon.length ? (
            <PosterRow testid="shared-canon-row">
              {blend.sharedCanon.slice(0, 24).map((f) => (
                <div key={f.uri} className="w-[120px] shrink-0">
                  <PosterCard title={f.title} year={f.year} overlap poster={posters[f.uri]} width={120} />
                  <div className="mt-1 space-y-0.5 font-mono text-[11px] tabular-nums">
                    <RatingLine name={nameA} rating={f.ratingA} tone="red" />
                    <RatingLine name={nameB} rating={f.ratingB} tone="gold" />
                  </div>
                </div>
              ))}
            </PosterRow>
          ) : (
            <Empty>No films you both love yet.</Empty>
          )}
        </Field>

        <Tear />

        {/* Disputes */}
        <Field testid="section-disputes">
          <Kicker>Worth the argument</Kicker>
          <H3>The disputes</H3>
          <Sub>Both rated these — you just couldn't disagree more.</Sub>
          <div className="mt-3">
            {blend.disputes.length ? (
              blend.disputes.map((d) => (
                <div
                  key={d.uri}
                  data-testid="dispute-card"
                  className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-divider py-3"
                >
                  <span className="flex-1 basis-full font-semibold text-ink-900 sm:basis-auto">
                    {d.title} {d.year ? <span className="font-mono text-[11px] text-ink-400">{d.year}</span> : null}
                  </span>
                  <span className="font-mono text-xs font-bold text-red-600">{firstName(nameA)} ★{d.ratingA.toFixed(1)}</span>
                  <span className="font-mono text-[11px] text-ink-400">↔ {d.gap.toFixed(1)}</span>
                  <span className="font-mono text-xs font-bold text-gold-600">{firstName(nameB)} ★{d.ratingB.toFixed(1)}</span>
                  <ShareDisputeButton data={{ nameA, nameB, title: d.title, year: d.year, ratingA: d.ratingA, ratingB: d.ratingB }} />
                </div>
              ))
            ) : (
              <Empty>No head-to-head disagreements — suspiciously aligned.</Empty>
            )}
          </div>
        </Field>

        <Tear />

        {/* Cross-recs, both directions */}
        <Field testid="section-crossrecs">
          <Kicker>One loves it, one hasn't seen it</Kicker>
          <H3>Cross-recs</H3>
          <p className="mono-meta mb-2 mt-3 normal-case tracking-normal text-ink-500">
            Films {firstName(nameA)} loved that {firstName(nameB)} hasn't seen
          </p>
          <CrossRecRow items={blend.crossRecsAToB} posters={posters} />
          <p className="mono-meta mb-2 mt-5 normal-case tracking-normal text-ink-500">
            Films {firstName(nameB)} loved that {firstName(nameA)} hasn't seen
          </p>
          <CrossRecRow items={blend.crossRecsBToA} posters={posters} />
        </Field>

        <Tear />

        {/* The overlap */}
        <Field testid="section-blend-footer">
          <Kicker>The overlap</Kicker>
          <H3>Two histories, one frame</H3>
          <div className="mt-4 flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
            <Venn onlyA={blend.counts.onlyAWatched} both={blend.counts.bothWatched} onlyB={blend.counts.onlyBWatched} nameA={firstName(nameA)} nameB={firstName(nameB)} />
            <div className="font-mono text-sm leading-relaxed text-ink-500">
              <p>{firstName(nameA)} averages <strong className="text-red-600">★{blend.averageA.toFixed(2)}</strong></p>
              <p>{firstName(nameB)} averages <strong className="text-gold-600">★{blend.averageB.toFixed(2)}</strong></p>
              {blend.harsherCritic && (
                <p className="mt-1">Harsher critic: <strong className="text-ink-900">{blend.harsherCritic === "A" ? firstName(nameA) : firstName(nameB)}</strong></p>
              )}
            </div>
          </div>
        </Field>

        {/* The verdict — one-line combined taste, GLM-written, lazy-loaded */}
        {(summary || summaryLoading) && (
          <>
            <Tear />
            <Field testid="section-summary">
              <Kicker>In one line</Kicker>
              {summary && !summaryLoading ? (
                <>
                  <p
                    key={summary}
                    data-testid="blend-summary"
                    className="reprint mt-2 font-display text-xl font-medium italic leading-snug text-ink-900 sm:text-2xl"
                  >
                    “{summary}”
                  </p>
                  {onRegenerateSummary && (
                    <button
                      type="button"
                      data-testid="action-regenerate-summary"
                      onClick={onRegenerateSummary}
                      className="mt-3 font-mono text-[11px] font-bold uppercase tracking-stamp text-ink-400 underline underline-offset-2 hover:text-red-600"
                    >
                      Don't like it? Get another ↻
                    </button>
                  )}
                </>
              ) : (
                <div className="mt-3" data-testid="blend-summary-loading">
                  <div className="h-5 w-3/4 rounded shimmer" />
                  <div className="mt-2 h-5 w-1/2 rounded shimmer" />
                  <p className="mono-meta mt-2 text-ink-400">Writing your blended taste…</p>
                </div>
              )}
            </Field>
          </>
        )}

        {/* Footer — barcode + attribution */}
        <div className="flex items-center gap-6 px-6 py-5 sm:px-10">
          <p className="mono-meta flex-1 leading-relaxed text-ink-400">
            Works with your Letterboxd export · Film data &amp; posters from TMDB. Not affiliated with Letterboxd or TMDB.
          </p>
          <div className="barcode hidden w-[200px] shrink-0 sm:block" aria-hidden />
        </div>
      </div>
    </div>
  );
}

// ---- Local field/ornament components ---------------------------------------

function Tear() {
  return (
    <div className="tdiv" aria-hidden>
      <span className="ntch nl" />
      <div className="perf" />
      <span className="ntch nr" />
    </div>
  );
}

function Field({ testid, children }: { testid?: string; children: React.ReactNode }) {
  return (
    <section data-testid={testid} className="px-6 py-7 sm:px-10">
      {children}
    </section>
  );
}

function Kicker({ children }: { children: React.ReactNode }) {
  return <div className="font-mono text-[11px] font-bold uppercase tracking-stamp text-red-600">{children}</div>;
}
function H3({ children, inline }: { children: React.ReactNode; inline?: boolean }) {
  return (
    <h2 className={`${inline ? "" : "mt-1.5"} font-display text-xl font-extrabold tracking-tightish text-ink-900 sm:text-2xl`}>
      {children}
    </h2>
  );
}
function Sub({ children }: { children: React.ReactNode }) {
  return <p className="mt-1 text-sm text-ink-500">{children}</p>;
}
function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-3 rounded-lg border border-dashed border-border-strong bg-paper-50 p-4 text-sm text-ink-500">{children}</p>
  );
}

function Count({ n, label, tone = "ink" }: { n: number; label: string; tone?: "ink" | "red" | "gold" | "muted" }) {
  const color =
    tone === "red" ? "text-red-600" : tone === "gold" ? "text-gold-600" : tone === "muted" ? "text-ink-300" : "text-ink-900";
  return (
    <div>
      <div className={`font-display text-[32px] font-extrabold leading-none tracking-tightish tabular-nums ${color}`}>
        {n.toLocaleString()}
      </div>
      <div className="mt-1.5 font-mono text-[10px] uppercase tracking-stamp text-ink-400">{label}</div>
    </div>
  );
}

function ScoreCol({ pct, label, desc, tone }: { pct: number; label: string; desc: string; tone: "red" | "gold" | "clay" }) {
  const color = tone === "red" ? "text-red-600" : tone === "gold" ? "text-gold-600" : "text-clay-500";
  return (
    <div className="flex-1 px-6 py-5 sm:px-10">
      <div className={`font-display text-4xl font-extrabold leading-none tracking-tightish tabular-nums ${color}`}>{pct}%</div>
      <div className="mt-2 text-[15px] font-bold text-ink-900">{label}</div>
      <div className="mt-1 text-xs leading-snug text-ink-500">{desc}</div>
    </div>
  );
}

function RatingLine({ name, rating, tone }: { name: string; rating: number; tone: "red" | "gold" }) {
  return (
    <div className="flex items-center justify-between gap-1">
      <span className="truncate text-ink-500" title={name}>{firstName(name)}</span>
      <span className={`shrink-0 ${tone === "red" ? "text-red-600" : "text-gold-600"}`}>★{rating.toFixed(1)}</span>
    </div>
  );
}

// A rubber-stamp verdict badge (bordered, rotated) — big enough to slam in.
function StampInk({ children }: { children: React.ReactNode }) {
  return (
    <span
      data-testid="stamp-badge"
      className="inline-flex -rotate-6 items-center rounded-md border-2 border-red-500 px-3.5 py-1.5 font-mono text-sm font-bold uppercase tracking-stamp text-red-400"
    >
      {children}
    </span>
  );
}

function Venn({ onlyA, both, onlyB, nameA, nameB }: { onlyA: number; both: number; onlyB: number; nameA: string; nameB: string }) {
  return (
    <svg viewBox="0 0 440 250" className="w-full max-w-md" data-testid="venn">
      <circle cx="150" cy="120" r="100" fill="#75182A" fillOpacity="0.18" />
      <circle cx="290" cy="120" r="100" fill="#E8945A" fillOpacity="0.26" />
      <text x="95" y="128" textAnchor="middle" fill="#5C1220" fontFamily="var(--font-display)" fontSize="30" fontWeight="800">{onlyA}</text>
      <text x="220" y="128" textAnchor="middle" fill="#1F1114" fontFamily="var(--font-display)" fontSize="30" fontWeight="800">{both}</text>
      <text x="345" y="128" textAnchor="middle" fill="#B5642F" fontFamily="var(--font-display)" fontSize="30" fontWeight="800">{onlyB}</text>
      <text x="110" y="245" textAnchor="middle" fill="#8D7178" fontFamily="var(--font-mono)" fontSize="11" letterSpacing="1.4">{nameA.toUpperCase()}</text>
      <text x="330" y="245" textAnchor="middle" fill="#8D7178" fontFamily="var(--font-mono)" fontSize="11" letterSpacing="1.4">{nameB.toUpperCase()}</text>
    </svg>
  );
}

function CopyLinkButton({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${typeof window !== "undefined" ? window.location.origin : "https://twoshot.xyz"}/b/${slug}`;
  async function share() {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: "Our Two Shot blend", url });
        return;
      } catch {
        /* fall through */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }
  return (
    <button
      type="button"
      data-testid="action-share-blend"
      onClick={share}
      className="inline-flex min-h-[40px] items-center gap-2 rounded-pill bg-gold-500 px-4 py-2 text-sm font-semibold text-ink-900 transition-colors hover:bg-gold-400"
    >
      {copied ? "Link copied!" : "Share this blend ↗"}
    </button>
  );
}

function CrossRecRow({
  items,
  posters,
}: {
  items: { uri: string; title: string; year?: number; rating: number; onOthersWatchlist: boolean }[];
  posters: Record<string, string>;
}) {
  if (!items.length) return <Empty>Nothing new to recommend here.</Empty>;
  return (
    <PosterRow>
      {items.map((f) => (
        <div key={f.uri} className="w-[120px] shrink-0">
          <PosterCard
            title={f.title}
            year={f.year}
            poster={posters[f.uri]}
            rating={f.rating}
            badge={f.onOthersWatchlist ? "On their list" : undefined}
            width={120}
          />
        </div>
      ))}
    </PosterRow>
  );
}

export type { SimilarityMetrics };
